// apps/web/src/components/Layout.tsx
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { User, ChevronDown } from 'lucide-react';

const DEFAULT_SITE = {
  site_name: '和春技術學院校友會',
  site_tagline: '凝聚校友情誼，傳承和春精神，共創美好未來。',
  contact_address: '桃園市龜山區文化一路568號',
  contact_phone: '(03) 328-1181',
  contact_website_label: 'hc.edu.tw',
  contact_website_url: 'https://www.hc.edu.tw',
  footer_copyright: '和春技術學院校友會',
  show_contact_address: '0',
  show_contact_phone: '0',
};

function isOn(v: string | undefined): boolean {
  return v === '1' || v === 'true';
}

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const { data: site } = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => api.getPublicSettings(),
    staleTime: 60_000,
  });

  const siteName = site?.site_name || DEFAULT_SITE.site_name;
  const tagline = site?.site_tagline || DEFAULT_SITE.site_tagline;
  const address = site?.contact_address || DEFAULT_SITE.contact_address;
  const phone = site?.contact_phone || DEFAULT_SITE.contact_phone;
  const webLabel = site?.contact_website_label || DEFAULT_SITE.contact_website_label;
  const webUrl = site?.contact_website_url || DEFAULT_SITE.contact_website_url;
  const copyright = site?.footer_copyright || DEFAULT_SITE.footer_copyright;
  // 開關關閉 = 不顯示（即使已填地址／電話）
  const showAddress = isOn(site?.show_contact_address ?? DEFAULT_SITE.show_contact_address);
  const showPhone = isOn(site?.show_contact_phone ?? DEFAULT_SITE.show_contact_phone);

  useEffect(() => {
    if (siteName) document.title = siteName;
  }, [siteName]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 換頁時關閉所有選單
  useEffect(() => {
    setAdminMenuOpen(false);
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // 點擊外部關閉
  useEffect(() => {
    if (!adminMenuOpen && !userMenuOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (adminMenuOpen && adminMenuRef.current && !adminMenuRef.current.contains(t)) {
        setAdminMenuOpen(false);
      }
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(t)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAdminMenuOpen(false);
        setUserMenuOpen(false);
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [adminMenuOpen, userMenuOpen]);

  const navLinks = [
    { path: '/directory', label: '校友名冊', icon: '👥' },
    { path: '/profile', label: '我的名片', icon: '📇' },
  ];

  // 副管理者僅見審核；主管理者另有會員管理／系統設定
  const adminLinks = [
    { path: '/admin', label: '審核儀表板', icon: '✅' },
    ...(user?.isOwner
      ? [
          { path: '/admin/users', label: '會員管理', icon: '👤' },
          { path: '/admin/settings', label: '系統設定', icon: '⚙️' },
        ]
      : []),
  ];

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
  };

  const adminActive = location.pathname.startsWith('/admin');

  return (
    <div className="min-h-screen flex flex-col">
      <header className={`site-header ${scrolled ? 'site-header-scrolled' : 'site-header-top'}`}>
        <nav className="container-custom" aria-label="主導覽">
          <div className="flex h-[4.25rem] items-center justify-between gap-3">
            <Link
              to="/directory"
              className="flex items-center gap-2.5 rounded-xl px-1 py-1 text-alumni-700 font-bold tracking-tight transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-alumni-600 focus-visible:ring-offset-2"
              aria-label={`${siteName}首頁`}
            >
              <img
                src="/logo.jpg"
                alt={siteName}
                className="h-10 w-10 rounded-full object-contain bg-white shadow-soft ring-1 ring-black/5"
                width={40}
                height={40}
              />
              <span className="hidden sm:block max-w-[11rem] truncate text-[0.95rem] lg:max-w-[16rem] lg:text-base" title={siteName}>
                {siteName}
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1.5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={
                    location.pathname === link.path || location.pathname.startsWith(link.path + '/')
                      ? 'nav-pill-active'
                      : 'nav-pill-idle'
                  }
                >
                  <span className="flex items-center gap-1.5">
                    <span className="opacity-90">{link.icon}</span> {link.label}
                  </span>
                </Link>
              ))}

              {user?.isAdmin && (
                <div
                  className="relative ml-1"
                  ref={adminMenuRef}
                  onMouseEnter={() => {
                    setAdminMenuOpen(true);
                    setUserMenuOpen(false);
                  }}
                  onMouseLeave={() => setAdminMenuOpen(false)}
                >
                  <button
                    type="button"
                    onClick={() => {
                      // 觸控裝置仍可用點擊開關
                      setAdminMenuOpen((v) => !v);
                      setUserMenuOpen(false);
                    }}
                    className={
                      adminActive || adminMenuOpen ? 'nav-pill-active' : 'nav-pill-idle'
                    }
                    aria-expanded={adminMenuOpen}
                    aria-haspopup="true"
                    aria-controls="admin-menu"
                    aria-label="管理員選單"
                  >
                    ⚙️ 管理
                  </button>

                  {/* hover / 點擊展開，無箭頭 */}
                  {adminMenuOpen && (
                    <div
                      id="admin-menu"
                      role="menu"
                      className="absolute right-0 top-full z-50 w-48 pt-1"
                    >
                      <div className="rounded-xl border border-gray-100 bg-white py-1 shadow-float dark:border-gray-700 dark:bg-gray-800 animate-scale-in">
                        {adminLinks.map((link) => (
                          <Link
                            key={link.path}
                            to={link.path}
                            role="menuitem"
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                              location.pathname === link.path
                                ? 'bg-alumni-50 font-medium text-alumni-700 dark:bg-alumni-900/30 dark:text-alumni-300'
                                : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/80'
                            }`}
                            onClick={() => setAdminMenuOpen(false)}
                          >
                            {link.icon} {link.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen((v) => !v);
                      setAdminMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-alumni-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                    aria-label="使用者選單"
                  >
                    <div className="w-8 h-8 rounded-full bg-alumni-600 flex items-center justify-center text-white font-medium text-sm">
                      {user.name.charAt(0)}
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {user.name}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {userMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 py-1 z-50 animate-scale-in"
                    >
                      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{user.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {user.email || user.lineUserId || 'LINE 登入'}
                        </p>
                        {user.isOwner && (
                          <span className="badge badge-yellow mt-1">主管理者</span>
                        )}
                        {user.isModerator && (
                          <span className="badge badge-green mt-1">副管理者</span>
                        )}
                      </div>
                      <Link
                        to="/profile"
                        role="menuitem"
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4" /> 我的名片
                      </Link>
                      <Link
                        to="/directory"
                        role="menuitem"
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        👥 校友名冊
                      </Link>
                      <hr className="my-1 border-gray-100 dark:border-gray-700" />
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                      >
                        🚪 登出
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="btn px-4 py-2 text-sm hidden sm:inline-flex">
                  LINE 登入
                </Link>
              )}

              <button
                type="button"
                className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={mobileMenuOpen ? '關閉選單' : '開啟選單'}
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div
              id="mobile-menu"
              className="md:hidden py-4 border-t border-gray-100 dark:border-gray-700 animate-slide-down"
            >
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname === link.path
                        ? 'bg-alumni-50 text-alumni-700 dark:bg-alumni-900/40 dark:text-alumni-300'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-alumni-700 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="flex items-center gap-3">
                      {link.icon} {link.label}
                    </span>
                  </Link>
                ))}

                {user?.isAdmin && (
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      管理員選單
                    </p>
                    {adminLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="px-4 py-3 rounded-lg text-base font-medium text-gray-600 hover:bg-gray-100 hover:text-alumni-700 dark:text-gray-300 dark:hover:bg-gray-800 flex items-center gap-3"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {link.icon} {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      <main className="flex-1 pt-[4.25rem] pb-14 sm:pb-16" id="main-content">
        <Outlet />
      </main>

      <footer className="mt-auto border-t border-gray-200/80 bg-white/90 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-950/90">
        <div className="container-custom py-10 sm:py-12">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8 text-center md:text-left">
            <div className="flex flex-col items-center md:items-start gap-3">
              <div className="flex items-center gap-3">
                <img
                  src="/logo.jpg"
                  alt={siteName}
                  className="h-12 w-12 rounded-full object-contain bg-white shadow-soft ring-1 ring-black/5"
                  width={48}
                  height={48}
                />
                <h3 className="font-bold text-lg text-alumni-700 dark:text-alumni-300">{siteName}</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line max-w-sm">
                {tagline}
              </p>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                快速連結
              </h4>
              <nav className="space-y-2.5">
                <Link
                  to="/directory"
                  className="block text-sm font-medium text-gray-700 hover:text-alumni-700 dark:text-gray-300 dark:hover:text-alumni-300"
                >
                  校友名冊
                </Link>
                <Link
                  to="/profile"
                  className="block text-sm font-medium text-gray-700 hover:text-alumni-700 dark:text-gray-300 dark:hover:text-alumni-300"
                >
                  我的名片
                </Link>
              </nav>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                聯絡資訊
              </h4>
              <address className="text-sm text-gray-600 dark:text-gray-400 not-italic space-y-1.5">
                {showAddress && address ? <p>📍 {address}</p> : null}
                {showPhone && phone ? (
                  <p>
                    📞{' '}
                    <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="hover:text-alumni-700">
                      {phone}
                    </a>
                  </p>
                ) : null}
                {webUrl ? (
                  <p>
                    🌐{' '}
                    <a
                      href={webUrl.startsWith('http') ? webUrl : `https://${webUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-alumni-700 underline underline-offset-2 hover:text-alumni-600 dark:text-alumni-300"
                    >
                      {webLabel || webUrl}
                    </a>
                  </p>
                ) : null}
                {!showAddress && !showPhone && !webUrl ? (
                  <p className="text-gray-400">聯絡方式請見後續公告</p>
                ) : null}
              </address>
            </div>
          </div>
          <hr className="my-8 border-gray-200 dark:border-gray-800" />
          <div className="text-center space-y-2">
            <p className="text-sm sm:text-[0.95rem] text-gray-700 dark:text-gray-300 font-semibold tracking-tight">
              © {new Date().getFullYear()} {copyright} All Rights Reserved.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              製作維護{' '}
              <a
                href="https://touchgold.com.tw/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-alumni-700 font-semibold underline underline-offset-2 hover:text-alumni-600 dark:text-alumni-300"
              >
                點金網創意有限公司
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
